import { all, call, delay, fork, put, select, takeLatest, takeLeading } from "typed-redux-saga";
import { Action, authorizationGateActions } from "./authorization-gate-store";
import { errorActions as errorActions } from "src/components/error-viewer/error-store";
import { baseUrl } from "src/utils/url-utils";
import { Token } from "./authorization-gate-types";
import { createGuid, hasValue } from "@itk/typescript-utils";
import { RootState } from "src/store";
import { deleteAccessToken, setAccessToken } from "src/backend/configure-openapi";
import { emailKey, tokenKey } from "./authorization-gate-utils";
import { ContactPersonService, ErrorCodes, LoginService, Shared_CreateResumeEmailKnownStates } from "src/backend/openapi/v1";
import { LoginFlowOtpOptions, LoginFlowState } from "./components/login-flow/login-flow-types";
import { deleteAllCache } from "src/utils/cache-utils";
import { LoginException } from "../error-viewer/error-types";

function* handleOtp(otpOption: LoginFlowOtpOptions, action: ReturnType<typeof authorizationGateActions.validateUserRequest>) {
  switch (otpOption) {
    case "sms":
      yield* call(LoginService.postApiV1LoginValidateRequest, action.payload.username, action.payload.requestId);
      break;
    case "email":
      yield* call(LoginService.postApiV1LoginLoginRequestViaEmail, action.payload.username, action.payload.requestId);
      break;
    default:
      const assertNever: never = otpOption;
      throw assertNever;
  }
}

export function* validateUserRequest(action: ReturnType<typeof authorizationGateActions.validateUserRequest>) {
  try {
    const loginModel = yield* call(LoginService.getApiV1LoginEmailExists, action.payload.username, action.payload.requestId);

    if (loginModel.publicLoginEmailKnownState === Shared_CreateResumeEmailKnownStates.EmailFoundOnPublicUser) {
      const otpOption: LoginFlowOtpOptions = loginModel.validatePublicAccessViaEmailOnly === true ? "email" : "sms";

      if (loginModel.passwordExists === true) {
        if (loginModel.forcePasswordResetOnNextLogin === true) {
          yield* handleOtp(otpOption, action);

          yield* put(authorizationGateActions.validateUserSuccess({ current: "otp", forcePasswordReset: true, otpOption }));
        } else if (loginModel.forceOTPOnNextLogin === true) {
          yield* handleOtp(otpOption, action);
          yield* put(authorizationGateActions.validateUserSuccess({ current: "otp-then-password", otpOption }));
        } else if (loginModel.disabledLogin === true) {
          const error: LoginException = {
            error: "Account is disabled",
            error_description: "Account is disabled",
            error_uri: "",
            error_code: ErrorCodes.PublicLoginFailedLoginDisabled
          };
          throw error;
        } else if (action.payload.isReset) {
          yield* handleOtp(otpOption, action);
          yield* put(authorizationGateActions.validateUserSuccess({ current: "forgot-password", stage: "code", otpOption }));
        }
      } else {
        yield* handleOtp(otpOption, action);
        yield* put(authorizationGateActions.validateUserSuccess({ current: "otp", forcePasswordReset: true, otpOption }));
      }
    } else if (loginModel.publicLoginEmailKnownState === Shared_CreateResumeEmailKnownStates.UnknownEmail) {
      yield* handleOtp("email", action);
      yield* put(authorizationGateActions.validateUserSuccess({ current: "otp", forcePasswordReset: true, otpOption: "email" }));
    } else if (loginModel.publicLoginEmailKnownState === Shared_CreateResumeEmailKnownStates.EmailFoundOnDEKOUser) {
      yield* put(authorizationGateActions.validateUserSuccess({ current: "email-found-on-dekouser" }));
    } else {
      yield* put(authorizationGateActions.validateUserSuccess({ current: "email" }));
    }
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(authorizationGateActions.validateOtpFail());
  }
}

export function* watchValidateUserRequest() {
  yield takeLatest(Action.ValidateUserRequest, validateUserRequest);
}

export function* validateOtpRequest(action: ReturnType<typeof authorizationGateActions.validateOtpRequest>) {
  try {
    const token = yield* call(LoginService.postApiV1LoginValidateRequest, action.payload.requestId, action.payload.passcode);
    if (hasValue(token)) {
      const loginFlowState = yield* select((state: RootState) => state.generalReducer.authorizationGateReducer.loginFlowState);

      if ((loginFlowState.current === "otp" || (loginFlowState.current === "forgot-password" && loginFlowState.stage === "code")) && hasValue(token.access_token)) {
        if (loginFlowState.current === "forgot-password" && loginFlowState.stage === "code") {
          yield* put(authorizationGateActions.validateOtpSuccess({ current: "new-password", stage: "reset-password" }));
        } else {
          yield* put(authorizationGateActions.validateOtpSuccess({ current: "new-password", stage: "new-user" }));
        }
        // Temporarily save the OTP access token, as we need it to change password before finalizing login
        // After login, it will be overwritten by the actual renewable access token
        yield* call(setAccessToken, token.access_token);
      } else if (loginFlowState.current === "otp-then-password") {
        yield* put(authorizationGateActions.validateOtpSuccess({ current: "password" }));
      }
      action.payload?.callback?.(true);
    }
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(authorizationGateActions.validateOtpFail());
    action.payload?.callback?.(false);
  }
}

export function* watchValidateOtpRequest() {
  yield takeLatest(Action.ValidateOtpRequest, validateOtpRequest);
}

export function* changePasswordRequest(action: ReturnType<typeof authorizationGateActions.changePasswordRequest>) {
  try {
    yield put(authorizationGateActions.setIsSubmitting(true));
    const loginFlowState: LoginFlowState = yield select((state: RootState) => state.generalReducer.authorizationGateReducer.loginFlowState);
    const result = yield* call(LoginService.postApiV1LoginChangePassword, undefined, { password: action.payload.newPassword });
    if (result.succeeded === true) {
      if (loginFlowState.current === "new-password" && loginFlowState.stage === "new-user") {
        yield* put(authorizationGateActions.setLoginFlowState({ current: "profile-info" }));
        const combinedPayload: Record<string, string> = { grant_type: "password", scope: "offline_access", username: action.payload.username, password: action.payload.newPassword };
        const body: string[] = [];
        for (const key in combinedPayload) {
          body.push(encodeURIComponent(key) + "=" + encodeURIComponent(combinedPayload[key]));
        }
        const tokenResponse = yield* call(fetch, baseUrl + "/connect/token", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.join("&")
        } satisfies RequestInit);
        if (tokenResponse.ok) {
          const token: Token = yield* call([tokenResponse, tokenResponse.json]);
          yield* call(setAccessToken, token.access_token);
        } else {
          throw yield* call([tokenResponse, tokenResponse.json]);
        }
      } else {
        yield* put(authorizationGateActions.logoutRequest(false));
        yield* put(authorizationGateActions.setLoginFlowState({ current: "completion", stage: "password-reset-success" }));
        yield* put(authorizationGateActions.validateUserRequest(action.payload.username, createGuid(), false));
      }
    } else {
      errorActions.addError(result.errorList);
    }
  } catch (error) {
    yield put(errorActions.addError(error));
  } finally {
    yield put(authorizationGateActions.setIsSubmitting(false));
  }
}

export function* watchChangePasswordRequest() {
  yield takeLatest(Action.ChangePasswordRequest, changePasswordRequest);
}

export function* setPasswordRequest(action: ReturnType<typeof authorizationGateActions.setPasswordRequest>) {
  try {
    const result = yield* call(LoginService.postApiV1LoginChangePassword, undefined, { password: action.payload.newPassword });
    if (result.succeeded === true) {
      yield* put(authorizationGateActions.setPasswordSuccess(action.payload.username, action.payload.newPassword));

      yield* put(authorizationGateActions.getLoginRequest(action.payload.username, action.payload.newPassword));
    } else {
      errorActions.addError(result.errorList);
    }
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchSetPasswordRequest() {
  yield takeLatest(Action.SetPasswordRequest, setPasswordRequest);
}

export function* getLoginRequest(action: ReturnType<typeof authorizationGateActions.getLoginRequest>) {
  try {
    const combinedPayload: Record<string, string> = { ...action.payload, grant_type: "password", scope: "offline_access" };
    const body: string[] = [];
    for (const key in combinedPayload) {
      body.push(encodeURIComponent(key) + "=" + encodeURIComponent(combinedPayload[key]));
    }
    const tokenResponse = yield* call(fetch, baseUrl + "/connect/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.join("&")
    } satisfies RequestInit);
    if (tokenResponse.ok) {
      const token: Token = yield* call([tokenResponse, tokenResponse.json]);
      yield* call(setAccessToken, token.access_token);
      const rememberMe = yield* select((state: RootState) => state.generalReducer.authorizationGateReducer.rememberMe);
      if (rememberMe) {
        yield* call([localStorage, localStorage.setItem], tokenKey, JSON.stringify(token));
        yield* call([localStorage, localStorage.setItem], emailKey, action.payload.username);
      }
      yield* put(authorizationGateActions.getLoginSuccess(token));
    } else {
      const loginModel = yield* call(LoginService.getApiV1LoginEmailExists, action.payload.username, createGuid());

      if (loginModel.publicLoginEmailKnownState === Shared_CreateResumeEmailKnownStates.EmailFoundOnPublicUser) {
        if (loginModel.disabledLogin === true) {
          // TODO : Throw the error here that the account is disabled
          const error: LoginException = {
            error: "Account is disabled",
            error_description: "Account is disabled",
            error_uri: "",
            error_code: ErrorCodes.PublicLoginFailedLoginDisabled
          };
          throw error;
        } else if (loginModel.passwordExists === true) {
          // TODO : Throw the error here that the password is incorrect
          const error: LoginException = {
            error: "Invalid password",
            error_description: "Invalid password",
            error_uri: "",
            error_code: ErrorCodes.PublicLoginInvalidPassword
          };
          throw error;
        }
      } else if (loginModel.publicLoginEmailKnownState === Shared_CreateResumeEmailKnownStates.UnknownEmail) {
        // TODO : Throw the error here that the email is not found
        const error: LoginException = {
          error: "Unknown user account",
          error_description: "Unknown user account",
          error_uri: "",
          error_code: ErrorCodes.PublicLoginFailedUnknownIdentity
        };
        throw error;
      } else {
        throw yield* call([tokenResponse, tokenResponse.json]);
      }
    }
  } catch (error) {
    yield* put(errorActions.addError(error));
    yield put(authorizationGateActions.setLoginState({ currentState: "error", errorCode: 1234 }));
    yield put(authorizationGateActions.getLoginFail());
  }
}

export function* watchGetLoginRequest() {
  yield* takeLatest(Action.GetLoginRequest, getLoginRequest);
}

export function* renewAccessTokenRequest(action: ReturnType<typeof authorizationGateActions.renewAccessTokenRequest>) {
  try {
    const combinedPayload: Record<string, string> = { grant_type: "refresh_token", refresh_token: action.payload.refreshToken };
    const body: string[] = [];
    for (const key in combinedPayload) {
      body.push(encodeURIComponent(key) + "=" + encodeURIComponent(combinedPayload[key]));
    }
    const tokenResponse = yield* call(fetch, baseUrl + "/connect/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.join("&")
    } satisfies RequestInit);

    if (tokenResponse.ok) {
      const token: Token = yield* call([tokenResponse, tokenResponse.json]);
      yield* call(setAccessToken, token.access_token);
      const rememberMe = yield* select((state: RootState) => state.generalReducer.authorizationGateReducer.rememberMe);
      if (rememberMe) {
        yield* call([localStorage, localStorage.setItem], tokenKey, JSON.stringify(token));
      }
      yield* put(authorizationGateActions.renewAccessTokenSuccess(token));
    } else {
      throw yield* call([tokenResponse, tokenResponse.json]);
    }
  } catch (error) {
    // If refreshing the token fails, ignore the error and sign out the user
    yield* put(authorizationGateActions.logoutRequest());
  }
}

export function* watchRenewAccessTokenRequest() {
  yield takeLatest(Action.RenewAccessTokenRequest, renewAccessTokenRequest);
}

export function* renewAccessToken(action: ReturnType<typeof authorizationGateActions.renewAccessTokenSuccess> | ReturnType<typeof authorizationGateActions.getLoginSuccess>) {
  try {
    const token = action.payload.token;
    if (!hasValue(token.refresh_token)) {
      return;
    }
    const renewIn = (token.expires_in - 120) * 1000; // ms before token renewal
    if (renewIn > 1000) {
      yield* delay(renewIn);
    }
    yield* put(authorizationGateActions.renewAccessTokenRequest(token.refresh_token));
  } catch (error) {
    yield put(errorActions.addError(error));
    yield put(authorizationGateActions.clear());
  }
}

export function* watchRenewAccessToken() {
  yield* takeLatest([Action.RenewAccessTokenSuccess, Action.GetLoginSuccess], renewAccessToken);
}

export function* logoutRequest(action: ReturnType<typeof authorizationGateActions.logoutRequest>) {
  try {
    const token = yield* select((state: RootState) => state.generalReducer.authorizationGateReducer.authorizationGateState);
    if (token.loading === "has-loaded" && hasValue(token.data.refresh_token)) {
      yield* call(fetch, baseUrl + "/connect/logout", {
        method: "POST",
        headers: { Authorization: `Bearer ${token.data.access_token}` }
      } satisfies RequestInit);
    }
  } finally {
    yield* call(deleteAccessToken);
    yield* call([localStorage, localStorage.clear]);
    deleteAllCache();
    if (action.payload.reload) {
      yield* put(authorizationGateActions.logoutSuccess());
    }
  }
}

export function* watchLogoutRequest() {
  yield* takeLatest(Action.LogoutRequest, logoutRequest);
}

export function* sendLoginRequestViaEmail(action: ReturnType<typeof authorizationGateActions.sendLoginRequestViaEmail>) {
  try {
    yield* call(LoginService.postApiV1LoginLoginRequestViaEmail, action.payload.email, action.payload.requestId);
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchSendLoginRequestViaEmail() {
  yield* takeLatest(Action.SendLoginRequestViaEmail, sendLoginRequestViaEmail);
}

export function* updateUserProfileRequest(action: ReturnType<typeof authorizationGateActions.updateUserProfileRequest>) {
  try {
    yield put(authorizationGateActions.setIsSubmitting(true));
    yield* call(ContactPersonService.putApiV1ContactPersonByIdentity, action.payload.profileDetails);
    yield* put(authorizationGateActions.setLoginFlowState({ current: "completion", stage: "new-user" }));
    yield* call(deleteAccessToken);
  } catch (error) {
    yield put(errorActions.addError(error));
  } finally {
    yield put(authorizationGateActions.setIsSubmitting(false));
  }
}

export function* watchUpdateUserProfileRequest() {
  yield takeLatest(Action.UpdateUserProfileRequest, updateUserProfileRequest);
}

export function* alternativeLoginRequest(action: ReturnType<typeof authorizationGateActions.alternativeLoginRequest>) {
  try {
    const token: Token = yield call(LoginService.postApiV1LoginDekoCustomerLoginOverride, action.payload.id);
    yield put(authorizationGateActions.alternativeLoginSuccess(token));
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchAlternativeLoginRequest() {
  yield takeLeading(Action.AlternativeLoginRequest, alternativeLoginRequest);
}

export function* authorizationGateSaga() {
  yield* all([
    fork(watchValidateUserRequest),
    fork(watchValidateOtpRequest),
    fork(watchChangePasswordRequest),
    fork(watchSetPasswordRequest),
    fork(watchGetLoginRequest),
    fork(watchRenewAccessTokenRequest),
    fork(watchRenewAccessToken),
    fork(watchLogoutRequest),
    fork(watchSendLoginRequestViaEmail),
    fork(watchUpdateUserProfileRequest),
    fork(watchAlternativeLoginRequest)
  ]);
}
